<template>
  <!-- Kalender → Kõik sissekanded in the new look (features.intraFrame),
       agreed on the mockup first: the filters as chips, the work orders grouped
       by day, newest first, with today's "Praegu" line between what is still
       ahead and what is done, and a quick look at one before opening it. -->
  <div class="feed">
    <div class="feed-head">
      <div>
        <div class="feed-cap">Kalender</div>
        <h1 class="feed-title">Kõik sissekanded</h1>
      </div>
      <div class="feed-count">{{ countText }}</div>
    </div>

    <section class="feed-filters" aria-label="Filtrid">
      <div class="feed-row">
        <label class="feed-search">
          <v-icon size="20">mdi-magnify</v-icon>
          <input v-model="q" type="search" placeholder="Klient, aadress või töökäsu tekst" aria-label="Otsi">
        </label>
        <div class="feed-seg" role="group" aria-label="Olek">
          <button
            v-for="option in statusOptions"
            :key="option.value"
            type="button"
            :class="{ on: status === option.value }"
            @click="status = option.value"
          >{{ option.text }}<small v-if="statusCounts">{{ statusCounts[option.value] }}</small></button>
        </div>
      </div>
      <div class="feed-row">
        <span class="feed-label">Tüüp</span>
        <div class="feed-chips">
          <button
            v-for="option in calendarCategoryOptions"
            :key="option.value"
            type="button"
            class="feed-chip"
            :class="{ on: categories.includes(option.value) }"
            @click="toggle(categories, option.value)"
          >{{ option.text }}</button>
        </div>
      </div>
      <div v-if="technics.length" class="feed-row">
        <span class="feed-label">Tehnik</span>
        <div class="feed-chips">
          <button
            v-for="technic in technics"
            :key="technic.id"
            type="button"
            class="feed-chip"
            :class="{ on: technicIds.includes(technic.id) }"
            @click="toggle(technicIds, technic.id)"
          ><span class="feed-ini" :style="initialsStyle(technic.color)">{{ initialsOf(technic.fullName) }}</span>{{ technic.fullName }}</button>
        </div>
      </div>
      <div class="feed-row">
        <span class="feed-label">Muu</span>
        <div class="feed-chips">
          <button type="button" class="feed-chip feed-chip--amber" :class="{ on: unpaid }" @click="unpaid = !unpaid">Maksmata (lõppenud)</button>
          <button
            v-for="option in objectTypeOptions"
            :key="option.value"
            type="button"
            class="feed-chip"
            :class="{ on: objectType === option.value }"
            @click="objectType = objectType === option.value ? null : option.value"
          >{{ option.value === objectTypes.CONTRACT ? 'Lepingulised' : 'Tavalised objektid' }}</button>
        </div>
        <span class="feed-dates">
          <span class="feed-label">Periood</span>
          <date-field v-model="from" plain aria-label="Alates" />
          –
          <date-field v-model="to" plain aria-label="Kuni" />
        </span>
      </div>
      <div v-if="activeText.length" class="feed-active">
        Filtrid: {{ activeText.join(' · ') }}
        <button type="button" class="feed-linkbtn" @click="resetFilters">Tühjenda</button>
      </div>
    </section>

    <!-- Kept in place while loading, so the list does not jump when a filter
         changes; the old rows stay until the new ones are here. -->
    <v-progress-linear :active="loading" indeterminate color="green" height="3" class="mt-2" />
    <div v-if="!loading && !events.length" class="feed-empty">
      Nende filtritega sissekandeid pole. Tühjenda mõni filter või vali pikem periood.
    </div>

    <section v-for="day in days" :key="day.key" class="feed-day">
      <div class="feed-dayhead">
        <span v-if="day.relative === 0" class="feed-today">Täna</span>
        <span v-else-if="day.relative === 1" class="feed-dn">homme</span>
        <span v-else-if="day.relative === -1" class="feed-dn">eile</span>
        <b>{{ day.weekday }}</b>
        <span class="feed-dn">{{ day.date }}</span>
        <span class="feed-n">{{ day.items.length }} {{ day.items.length === 1 ? 'töökäsk' : 'töökäsku' }}</span>
      </div>
      <div class="feed-rows">
        <template v-for="item in day.items" :key="item.key">
          <div v-if="item.now" class="feed-now"><span>Praegu {{ nowTime }}</span></div>
          <div
            v-else
            class="feed-item"
            :class="{ 'feed-item--deleted': item.event.status === 'deleted' }"
            :style="{ '--tc': (item.event.user && item.event.user.color) || '#9AAAA4' }"
            tabindex="0"
            @click="peek(item.event)"
            @keydown.enter="peek(item.event)"
          >
            <div class="feed-when">
              <b>{{ time(item.event.startDate) }}–{{ time(item.event.endDate) }}</b>
              <small>{{ duration(item.event) }}</small>
              <span v-if="item.event.status === 'deleted'" class="feed-deltag">{{ calendarDeleteReasonById(item.event.deleteReason).text }}</span>
            </div>
            <div class="feed-tech">
              <template v-if="item.event.user && item.event.user.fullName">
                <span class="feed-ini" :style="initialsStyle(item.event.user.color)">{{ initialsOf(item.event.user.fullName) }}</span>
                <span class="feed-ellipsis">{{ item.event.user.fullName }}<template v-if="item.event.helper && item.event.helper.fullName">, {{ item.event.helper.fullName }}</template></span>
              </template>
              <span v-else class="feed-muted">määramata</span>
            </div>
            <div class="feed-who">
              <span class="feed-cat" :class="'feed-cat--' + item.event.category">{{ calendarCategoryById(item.event.category).text }}</span>
              <div class="feed-client">{{ (item.event.client && item.event.client.fullName) || 'määramata' }}</div>
              <div class="feed-addr">{{ $address(item.event.object && item.event.object.fullAddress) }}</div>
              <div v-if="deviceSummary(item.event.object && item.event.object.devices)" class="feed-dev">{{ deviceSummary(item.event.object.devices) }}</div>
            </div>
            <div class="feed-noteCol">
              <div class="feed-note" :class="{ 'feed-muted': !item.event.comments }">{{ item.event.comments || 'Töökäsu tekst puudub' }}</div>
            </div>
            <div class="feed-payCol">
              <template v-if="item.event.status === 'past'">
                <span v-if="item.event.payment && item.event.payment.type === 0" class="feed-unpaid">Maksmata</span>
                <span v-else class="feed-pay">{{ paymentLabel(item.event.payment && item.event.payment.type) }}</span>
              </template>
              <span v-else-if="item.event.status === 'future'" class="feed-muted">—</span>
            </div>
            <div class="feed-acts">
              <button type="button" class="feed-linkbtn" @click.stop="addNote(item.event)">Lisa märkus</button>
            </div>
          </div>
        </template>
      </div>
    </section>

    <div v-if="events.length" class="feed-more">
      <span>Näidatud {{ events.length }} / {{ count }}</span>
      <v-btn v-if="events.length < count" variant="outlined" :loading="loading" @click="loadMore">Näita veel {{ Math.min(pageSize, count - events.length) }}</v-btn>
    </div>

    <!-- The quick look: the main facts, and the way into the work order. -->
    <v-dialog v-model="peekOpen" class="feed-sheet" transition="dialog-right-transition" max-width="440">
      <div v-if="peeked" class="feed-sheet__in" role="dialog" aria-label="Töökäsk">
        <button type="button" class="feed-x" aria-label="Sulge" @click="peekOpen = false">×</button>
        <div class="feed-cap">{{ calendarCategoryById(peeked.category).text }} · {{ statusLabel(peeked.status) }}</div>
        <h3 class="feed-sheet__title">{{ longDate(peeked.startDate) }}, {{ time(peeked.startDate) }}–{{ time(peeked.endDate) }}</h3>
        <dl class="feed-dl">
          <dt>Tehnik</dt>
          <dd class="d-flex align-center ga-2">
            <template v-if="peeked.user && peeked.user.fullName">
              <span class="feed-ini" :style="initialsStyle(peeked.user.color)">{{ initialsOf(peeked.user.fullName) }}</span>{{ peeked.user.fullName }}<template v-if="peeked.helper && peeked.helper.fullName">, {{ peeked.helper.fullName }}</template>
            </template>
            <template v-else>—</template>
          </dd>
          <dt>Klient</dt>
          <dd><client-link :client="peeked.client" /></dd>
          <dt>Aadress</dt>
          <dd><object-link :object="peeked.object" /><template v-if="peeked.object && peeked.object.type === objectTypes.CONTRACT"> · lepinguline</template></dd>
          <template v-if="deviceSummary(peeked.object && peeked.object.devices)">
            <dt>Seadmed</dt>
            <dd>{{ deviceSummary(peeked.object.devices) }}</dd>
          </template>
          <dt>Töökäsk</dt>
          <dd class="feed-pre">{{ peeked.comments || '—' }}</dd>
          <dt>Makse</dt>
          <dd>{{ peeked.status === 'past' ? paymentLabel(peeked.payment && peeked.payment.type) : '—' }}</dd>
          <template v-if="peeked.status === 'deleted'">
            <dt>Tühistatud</dt>
            <dd>{{ calendarDeleteReasonById(peeked.deleteReason).text }}</dd>
          </template>
        </dl>
        <div class="d-flex flex-wrap ga-2">
          <v-btn color="green" variant="flat" @click="openEvent(peeked)">Ava töökäsk</v-btn>
          <v-btn variant="outlined" @click="addNote(peeked)">Lisa märkus</v-btn>
        </div>
      </div>
    </v-dialog>

    <note-form
      :object="note.object"
      :objectId="note.objectId"
      :event="note.event"
      v-model:visible="note.showForm"
    />
    <v-dialog v-model="eventOpen" :max-width="features.eventPlate ? 880 : 700">
      <event v-if="eventOpen" mode="view" :eventId="eventId" @close="eventOpen = false" />
    </v-dialog>
  </div>
</template>

<script>
import api from '@/api'
import dayjs from 'dayjs'
import { mapActions, mapState } from 'vuex'
import helpers from '@/plugins/helpersMixin'
import { deviceSummary } from '@/plugins/devices'
import { initials } from '@/components/frame/frameLinks'

const PAYMENT_TYPES = ['Maksmata', 'Arvega', 'Sularaha', 'Tasuta']
const WEEKDAYS = ['Pühapäev', 'Esmaspäev', 'Teisipäev', 'Kolmapäev', 'Neljapäev', 'Reede', 'Laupäev']
const PAGE_SIZE = 25

export default {
  mixins: [ helpers ],
  data () {
    // The Töölaud's cards link here with their own filter in the address
    // (Dashboard.vue unpaidLink): it is read once, as the old list did.
    const query = this.$route.query
    const ints = value => [].concat(value || []).map(Number).filter(n => !isNaN(n))
    return {
      events: [],
      count: 0,
      statusCounts: null,
      loading: false,
      pageSize: PAGE_SIZE,
      technics: [],
      q: '',
      status: 'all',
      categories: ints(query.category),
      technicIds: ints(query.technicId),
      unpaid: query.paymentType === '0' || query.paymentType === 0,
      objectType: null,
      from: query.startDate && query.startDate !== '2012-02-14' ? query.startDate : '',
      to: '',
      // From a card only; each has a chip in "Filtrid" and goes with Tühjenda.
      daysSince: parseInt(query.daysSince) || null,
      order: query.order === 'oldest' ? 'oldest' : null,
      hideDeleted: query.deleted === 'false',
      peekOpen: false,
      peeked: null,
      eventOpen: false,
      eventId: null,
      note: { object: null, objectId: null, event: null, showForm: false },
      now: new Date(),
      searchTimer: null,
      // A newer request supersedes an older one still on its way.
      requestNo: 0
    }
  },
  computed: {
    ...mapState(['features']),
    statusOptions () {
      return [
        { value: 'all', text: 'Kõik' },
        { value: 'future', text: 'Tulevikus' },
        { value: 'past', text: 'Lõppenud' },
        { value: 'deleted', text: 'Kustutatud' }
      ]
    },
    params () {
      const params = {
        type: 2,
        counts: true,
        status: this.status === 'all' ? undefined : this.status,
        // "Kõik" has always included the deleted ones; a card can ask not to.
        deleted: this.status === 'deleted' || (this.status === 'all' && !this.hideDeleted) ? true : undefined,
        q: this.q.trim() || undefined,
        category: this.categories.length ? this.categories : undefined,
        technicIds: this.technicIds.length ? this.technicIds : undefined,
        unpaid: this.unpaid || undefined,
        objectType: this.objectType === null ? undefined : this.objectType,
        startDate: this.from || (this.to ? '2000-01-01' : undefined),
        endDate: this.to || undefined,
        daysSince: this.daysSince || undefined,
        order: this.order || undefined
      }
      return Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined))
    },
    filterKey () {
      return JSON.stringify(this.params)
    },
    countText () {
      const range = this.from || this.to
        ? ' · ' + (this.from ? dayjs(this.from).format('DD.MM.YYYY') : '…') + '–' + (this.to ? dayjs(this.to).format('DD.MM.YYYY') : '…')
        : ''
      return this.count + ' sissekannet' + range
    },
    activeText () {
      const parts = []
      if (this.q.trim()) parts.push('otsing "' + this.q.trim() + '"')
      if (this.categories.length) parts.push(this.categories.map(c => this.calendarCategoryById(c).text).join(', '))
      if (this.technicIds.length) parts.push(this.technicIds.map(id => (this.technics.find(t => t.id === id) || { fullName: '#' + id }).fullName).join(', '))
      if (this.unpaid) parts.push('maksmata')
      if (this.objectType !== null) parts.push(this.objectType === this.objectTypes.CONTRACT ? 'lepingulised' : 'tavalised')
      if (this.daysSince) parts.push('üle ' + this.daysSince + ' päeva tagasi')
      if (this.order === 'oldest') parts.push('vanimad enne')
      if (this.hideDeleted) parts.push('ilma kustutatuteta')
      return parts
    },
    nowTime () {
      return dayjs(this.now).format('HH:mm')
    },
    // The loaded work orders by day, in the order they came, with the
    // "Praegu" line in today's group where the time passes.
    days () {
      const today = dayjs(this.now).startOf('day')
      const days = []
      for (const event of this.events) {
        const start = dayjs(event.startDate)
        const key = start.format('YYYY-MM-DD')
        let day = days[days.length - 1]
        if (!day || day.key !== key) {
          day = {
            key,
            weekday: WEEKDAYS[start.day()],
            date: start.format('DD.MM.YYYY'),
            relative: start.startOf('day').diff(today, 'day'),
            items: []
          }
          days.push(day)
        }
        day.items.push({ key: event.id, event })
      }
      for (const day of days) {
        if (day.relative !== 0) continue
        const newestFirst = this.order !== 'oldest'
        const at = day.items.findIndex(item => newestFirst
          ? !dayjs(item.event.startDate).isAfter(this.now)
          : dayjs(item.event.startDate).isAfter(this.now))
        day.items.splice(at === -1 ? day.items.length : at, 0, { key: 'now', now: true })
      }
      return days
    }
  },
  watch: {
    // Typing waits a moment; every other filter asks at once.
    q () {
      clearTimeout(this.searchTimer)
      this.searchTimer = setTimeout(() => this.load(), 300)
    },
    filterKey (value, old) {
      if (JSON.parse(value).q === JSON.parse(old).q) this.load()
    }
  },
  methods: {
    ...mapActions(['showNotification']),
    deviceSummary,
    initialsOf: initials,
    initialsStyle (color) {
      if (!color) return { background: '#9AAAA4', color: '#fff' }
      const hex = color.replace('#', '')
      const full = hex.length === 3 ? hex.split('').map(c => c + c).join('') : hex
      const [r, g, b] = [0, 2, 4].map(i => parseInt(full.slice(i, i + 2), 16))
      return { background: color, color: (0.299 * r + 0.587 * g + 0.114 * b) > 150 ? '#16322A' : '#fff' }
    },
    async load (append = false) {
      const no = ++this.requestNo
      this.loading = true
      this.now = new Date()
      try {
        const result = await api.getEvents({ ...this.params, limit: PAGE_SIZE, offset: append ? this.events.length : 0 })
        if (no !== this.requestNo) return
        // By id: a work order added or removed between two pages shifts the
        // offset, and the next page then repeats a row already shown.
        const have = new Set(append ? this.events.map(event => event.id) : [])
        this.events = append ? this.events.concat(result.events.filter(event => !have.has(event.id))) : result.events
        this.count = result.count
        this.statusCounts = result.statusCounts || null
      } catch (err) {
        if (no === this.requestNo) this.showNotification({ text: err.message, color: 'red' })
      } finally {
        if (no === this.requestNo) this.loading = false
      }
    },
    loadMore () {
      this.load(true)
    },
    toggle (list, value) {
      const at = list.indexOf(value)
      at === -1 ? list.push(value) : list.splice(at, 1)
    },
    resetFilters () {
      this.q = ''
      this.status = 'all'
      this.categories = []
      this.technicIds = []
      this.unpaid = false
      this.objectType = null
      this.daysSince = null
      this.order = null
      this.hideDeleted = false
    },
    time (date) {
      return dayjs(date).format('HH:mm')
    },
    longDate (date) {
      const d = dayjs(date)
      return WEEKDAYS[d.day()].toLowerCase() + ' ' + d.format('DD.MM.YYYY')
    },
    duration (event) {
      const minutes = Math.max(0, dayjs(event.endDate).diff(dayjs(event.startDate), 'minute'))
      const h = Math.floor(minutes / 60)
      const m = minutes % 60
      return (h ? h + ' h' : '') + (h && m ? ' ' : '') + (m ? m + ' min' : '') || '0 min'
    },
    paymentLabel (type) {
      return PAYMENT_TYPES[type] || '—'
    },
    statusLabel (status) {
      return { future: 'tulevikus', past: 'lõppenud', deleted: 'kustutatud' }[status] || ''
    },
    peek (event) {
      this.peeked = event
      this.peekOpen = true
    },
    openEvent (event) {
      this.peekOpen = false
      this.eventId = event.id
      this.eventOpen = true
    },
    addNote (event) {
      this.peekOpen = false
      this.note.object = event.object
      this.note.objectId = event.object && event.object.id
      this.note.event = event
      this.note.showForm = true
    }
  },
  mounted () {
    this.load()
    api.getTechnics().then(({ users }) => {
      this.technics = users.filter(user => user.active !== false)
        .sort((a, b) => (a.fullName || '').localeCompare(b.fullName || '', 'et'))
    }).catch(() => {})
  },
  beforeUnmount () {
    clearTimeout(this.searchTimer)
  }
}
</script>

<style>
/* The quick look is teleported out of the page, so its sheet is styled here
   unscoped, on its own class. */
.feed-sheet.v-dialog { justify-content: flex-end; align-items: stretch; }
.feed-sheet.v-dialog > .v-overlay__content { margin: 0 !important; height: 100%; max-height: 100% !important; width: 440px; max-width: 100% !important; }
.feed-sheet__in { background: #fff; color: #16322A; height: 100%; overflow: auto; padding: 22px; font-family: 'Archivo Variable', 'Archivo', 'Helvetica Neue', Helvetica, Arial, sans-serif; }
.feed-sheet__title { font-size: 20px; font-weight: 600; margin: 4px 0 2px; }
.feed-sheet .feed-cap { font-size: 12px; letter-spacing: .08em; text-transform: uppercase; color: #5B6B66; font-weight: 600; }
.feed-sheet .feed-x { float: right; border: 0; background: none; font-size: 24px; cursor: pointer; color: #5B6B66; line-height: 1; }
.feed-sheet .feed-dl { display: grid; grid-template-columns: 100px minmax(0, 1fr); gap: 8px 12px; margin: 16px 0 20px; }
.feed-sheet .feed-dl dt { color: #5B6B66; font-size: 13px; }
.feed-sheet .feed-dl dd { margin: 0; min-width: 0; }
.feed-sheet .feed-pre { white-space: pre-line; }
.feed-sheet .feed-dl a { color: #1F5FD1; text-decoration: none; font-weight: 600; }
.feed-sheet .feed-dl a:hover { text-decoration: underline; }
.feed-sheet .feed-ini { display: inline-grid; place-items: center; width: 22px; height: 22px; border-radius: 50%; font-size: 10px; font-weight: 700; flex: none; }
</style>

<style scoped>
.feed {
  --f-ground: #F4F6F5; --f-surface: #FFFFFF; --f-ink: #16322A; --f-ink-2: #3E504A; --f-ink-3: #5B6B66;
  --f-line: #C9D2CE; --f-line-soft: #E1E7E4; --f-green: #2E7D32; --f-green-dark: #1B5E20; --f-green-bg: #EAF3EA;
  --f-blue: #1F5FD1; --f-blue-bg: #EEF3FC; --f-amber: #8F5400; --f-amber-bg: #FFF4E0; --f-red: #B3261E; --f-red-bg: #FCEDEC;
  --f-now: #D93025;
  font-family: 'Archivo Variable', 'Archivo', 'Helvetica Neue', Helvetica, Arial, sans-serif;
  color: var(--f-ink);
  padding-bottom: 40px;
}
.feed button, .feed input { font: inherit; color: inherit; }
.feed-head { display: flex; flex-wrap: wrap; align-items: end; justify-content: space-between; gap: 12px; margin-bottom: 16px; }
.feed-cap { font-size: 12px; letter-spacing: .08em; text-transform: uppercase; color: var(--f-ink-3); font-weight: 600; }
.feed-title { font-size: 30px; font-weight: 600; margin: 2px 0 0; letter-spacing: -.01em; line-height: 1.2; }
.feed-count { color: var(--f-ink-3); font-variant-numeric: tabular-nums; }

.feed-filters { background: var(--f-surface); border: 1px solid var(--f-line-soft); border-radius: 10px; padding: 14px; display: grid; gap: 12px; margin-bottom: 6px; }
.feed-row { display: flex; flex-wrap: wrap; gap: 10px 16px; align-items: center; }
.feed-search { flex: 1 1 280px; min-width: 0; display: flex; align-items: center; gap: 8px; border: 1px solid var(--f-line); border-radius: 8px; padding: 0 12px; color: var(--f-ink-3); }
.feed-search input { border: 0; outline: 0; background: transparent; padding: 9px 0; width: 100%; min-width: 0; color: var(--f-ink); }
.feed-seg { display: inline-flex; flex-wrap: wrap; border: 1px solid var(--f-line); border-radius: 8px; overflow: hidden; }
.feed-seg button { border: 0; background: transparent; padding: 7px 12px; cursor: pointer; font-weight: 500; color: var(--f-ink-2); font-variant-numeric: tabular-nums; }
.feed-seg button + button { border-left: 1px solid var(--f-line); }
.feed-seg button.on { background: var(--f-ink); color: #fff; }
.feed-seg small { opacity: .7; margin-left: 4px; }
.feed-label { font-size: 12px; color: var(--f-ink-3); font-weight: 600; letter-spacing: .03em; min-width: 54px; }
.feed-chips { display: flex; flex-wrap: wrap; gap: 6px; }
.feed-chip { border: 1px solid var(--f-line); background: var(--f-surface); border-radius: 999px; padding: 4px 11px; cursor: pointer; font-size: 13px; display: inline-flex; align-items: center; gap: 6px; }
.feed-chip.on { background: var(--f-green-bg); border-color: var(--f-green); color: var(--f-green-dark); font-weight: 600; }
.feed-chip--amber.on { background: var(--f-amber-bg); border-color: var(--f-amber); color: var(--f-amber); }
.feed-ini { display: inline-grid; place-items: center; width: 22px; height: 22px; border-radius: 50%; font-size: 10px; font-weight: 700; flex: none; letter-spacing: .02em; }
.feed-dates { display: inline-flex; align-items: center; gap: 6px; flex-wrap: wrap; }
.feed-dates .feed-label { min-width: 0; }
.feed-dates :deep(input) { border: 1px solid var(--f-line); border-radius: 6px; padding: 5px 30px 5px 8px; background: var(--f-surface); width: 140px; }
.feed-active { font-size: 13px; color: var(--f-ink-3); }
.feed .feed-linkbtn { border: 0; background: none; color: var(--f-blue); cursor: pointer; padding: 2px 0; font-weight: 500; }
.feed-empty { background: var(--f-surface); border: 1px dashed var(--f-line); border-radius: 10px; padding: 30px; text-align: center; color: var(--f-ink-3); margin-top: 16px; }

.feed-day { margin-top: 18px; }
.feed-dayhead { position: sticky; top: var(--v-layout-top, 0px); z-index: 2; background: var(--f-ground); display: flex; align-items: baseline; gap: 10px; padding: 8px 2px; border-bottom: 1px solid var(--f-line); }
.feed-dayhead b { font-size: 16px; font-weight: 600; }
.feed-dn { color: var(--f-ink-3); font-variant-numeric: tabular-nums; }
.feed-today { background: var(--f-ink); color: #fff; font-size: 11px; font-weight: 700; letter-spacing: .06em; text-transform: uppercase; border-radius: 4px; padding: 1px 6px; }
.feed-n { margin-left: auto; color: var(--f-ink-3); font-size: 13px; }
.feed-rows { background: var(--f-surface); border: 1px solid var(--f-line-soft); border-top: 0; border-radius: 0 0 10px 10px; }
.feed-item { display: grid; grid-template-columns: 112px 180px minmax(0, 1.3fr) minmax(0, 1fr) 96px 100px; gap: 14px; align-items: start; padding: 11px 14px; border-top: 1px solid var(--f-line-soft); cursor: pointer; position: relative; }
.feed-rows > .feed-item:first-child { border-top: 0; }
.feed-item:hover { background: var(--f-ground); }
.feed-item::before { content: ''; position: absolute; left: 0; top: 8px; bottom: 8px; width: 3px; border-radius: 0 3px 3px 0; background: var(--tc); }
.feed-when { font-variant-numeric: tabular-nums; }
.feed-when b { font-weight: 600; font-size: 15px; }
.feed-when small { display: block; color: var(--f-ink-3); font-size: 12px; }
.feed-tech { display: flex; align-items: center; gap: 8px; min-width: 0; }
.feed-ellipsis { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.feed-who { min-width: 0; }
.feed-client { font-weight: 600; color: var(--f-blue); }
.feed-addr { color: var(--f-ink-2); }
.feed-dev { color: var(--f-ink-3); font-size: 12px; margin-top: 2px; }
.feed-cat { display: inline-block; font-size: 11px; font-weight: 700; letter-spacing: .04em; text-transform: uppercase; padding: 1px 7px; border-radius: 4px; background: var(--f-line-soft); color: var(--f-ink-2); margin-bottom: 3px; }
.feed-cat--1 { background: var(--f-red-bg); color: var(--f-red); }
.feed-cat--5 { background: var(--f-blue-bg); color: var(--f-blue); }
.feed-cat--3 { background: var(--f-amber-bg); color: var(--f-amber); }
.feed-note { color: var(--f-ink-2); display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; white-space: pre-line; }
.feed-muted { color: var(--f-ink-3) !important; }
.feed-pay { font-size: 12px; font-weight: 600; }
.feed-unpaid { font-size: 12px; font-weight: 600; color: var(--f-amber); background: var(--f-amber-bg); padding: 2px 8px; border-radius: 999px; display: inline-block; }
.feed-acts { text-align: right; }
.feed-item--deleted .feed-when b, .feed-item--deleted .feed-client, .feed-item--deleted .feed-addr { text-decoration: line-through; color: var(--f-ink-3); }
.feed-item--deleted::before { background: var(--f-red); }
.feed-deltag { display: inline-block; font-size: 11px; font-weight: 700; color: var(--f-red); background: var(--f-red-bg); border-radius: 4px; padding: 1px 6px; margin-top: 3px; }
.feed-now { position: relative; height: 0; }
.feed-now::after { content: ''; position: absolute; left: 0; right: 0; top: -1px; border-top: 2px solid var(--f-now); }
.feed-now span { position: absolute; left: 14px; top: 0; z-index: 1; background: var(--f-now); color: #fff; font-size: 11px; font-weight: 700; padding: 1px 7px; border-radius: 999px; transform: translateY(-50%); }
.feed-more { display: flex; flex-wrap: wrap; justify-content: space-between; align-items: center; gap: 10px; margin-top: 16px; color: var(--f-ink-3); font-variant-numeric: tabular-nums; }

@media (max-width: 1100px) {
  .feed-item { grid-template-columns: 96px minmax(0, 1fr) minmax(0, 1fr) 90px; }
  .feed-tech { grid-column: 2; grid-row: 2; }
  .feed-noteCol { grid-column: 3; grid-row: 2; }
  .feed-payCol { grid-column: 4; grid-row: 1; }
  .feed-acts { grid-column: 4; grid-row: 2; }
}
@media (max-width: 760px) {
  .feed-item { grid-template-columns: 76px minmax(0, 1fr); gap: 6px 12px; }
  .feed-tech, .feed-noteCol, .feed-payCol, .feed-acts { grid-column: 2; grid-row: auto; text-align: left; }
  .feed-label { min-width: 0; width: 100%; }
}
</style>
